import React from 'react';
import { PageData, PageDataParams } from './page-data';
import { ProblemOption } from '../common/problem-option';
import { stripHtml } from '../common/helpers';
import {
  SFFProblemType,
  TagText,
  RawInputPageData,
  Hints,
  TagResult,
} from '../common/page-data';
import { parseParagraph } from '../common/tsx-helpers';
import { renderToString } from 'react-dom/server';
import styled from '@emotion/styled';
import { addPeriod } from '../utils/utils';

const Description = styled.div`
  comment: Description;
  grid-area: Description;
  flex-grow: 0;
  flex-shrink: 0;
  padding: 1em;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  user-select: none;
`;
const Type1Description = styled.div`
  comment: Type1Description;
  grid-area: Description;
  flex-grow: 0;
  flex-shrink: 0;
  padding: 1em;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  user-select: none;
  justify-content: center;
`;
export class InputData extends PageData {
  private _options: ProblemOption[];
  public get options(): ProblemOption[] {
    return this._options;
  }
  protected _hints: Hints;
  public get hints(): Hints {
    return this._hints;
  }
  public get isMultiChoice(): boolean {
    return this._problemType === 'multiChoice';
  }
  public get textDescription(): string {
    return stripHtml(renderToString(this.descriptionElement));
  }

  public get rawData(): RawInputPageData {
    const options = this._options.map((v) => v.getRawData());
    const playStats = this.playStats;

    const raw: RawInputPageData = {
      ...(this._rawPageData as RawInputPageData),
      options,
      playStats,
    };

    return raw;
  }

  public get tagResults(): TagResult[] {
    const tagResults: TagResult[] = [];

    this.options.forEach((option) => {
      const correct = option.isCorrectResponse === option.isSelected;
      const tags = option.tags;

      if (tags) {
        tags.forEach((tag) => {
          tagResults.push({ tagId: tag.id, name: tag.name, correct });
        });
      }
    });

    return tagResults;
  }

  public reset(): void {
    this._options.forEach((option) => {
      option.isSelected = false;
    });
  }

  public constructor(params: PageDataParams) {
    super(params);

    const { rawPageData } = params;
    const { options, problemQuality, type, skill, hints, problemType } =
      rawPageData as RawInputPageData;

    this._problemType = problemType;
    this._problemQuality = problemQuality;
    this._hints = hints;
    this._options = options
      ? options.map(
          (v): ProblemOption =>
            new ProblemOption(v, problemQuality, type, skill)
        )
      : [];
  }

  public get descriptionElement(): JSX.Element {
    let ret = <div />;

    // Need to see how many correct options are there.
    const numCorrectOptions = this.options.filter(value => value.isCorrectResponse).length;
    const isMultiChoice = this.isMultiChoice && numCorrectOptions > 1;
    const skillName = this.skill.name;

    switch (this.type) {
      case SFFProblemType.Type1: {
        const description = (
          <span>
            Select <b>all</b> statements that are a{' '}
            <b>{this.problemQuality ? 'good' : 'poor'}</b> demonstration of{' '}
            {skillName.toLocaleLowerCase()}.
          </span>
        );
        ret = <Type1Description>{description}</Type1Description>;
        break;
      }

      case SFFProblemType.Type2: {
        const description = (
          <span>
            You could respond in the following ways. Choose{' '}
            {isMultiChoice ? <b>all</b> : 'the'} statement
            {isMultiChoice ? 's ' : ' '} that{' '}
            {this.problemQuality ? <b>best</b> : <b>poorly</b>} demonstrate
            {this.isMultiChoice ? ' ' : 's '}{' '}
            {addPeriod(this.skill.name).toLocaleLowerCase()}
          </span>
        );
        ret = <Description>{description}</Description>;
        break;
      }

      case SFFProblemType.Type3: {
        const multiChoice = isMultiChoice ? (
          <span>
            Select <b>all</b> that apply.
          </span>
        ) : (
          <span>
            (Choose <b>best</b> answer)
          </span>
        );

        ret = (
          <Description>
            <p>
              What makes <b>this response</b> a{' '}
              <b>{this.problemQuality ? 'good' : 'poor'}</b> demonstration of{' '}
              {skillName.toLocaleLowerCase()}? {multiChoice}
            </p>
          </Description>
        );
        break;
      }

      case SFFProblemType.Type4: {
        const question: string =
          typeof this.question === 'string'
            ? this.question
            : this.question.declarative;
        ret = <Description>
          <p>Select the response that demonstrates {skillName.toLocaleLowerCase()} <i>{this.problemQuality ? '' : ' poorly'}</i> because it <b>{question}</b>.</p>
        </Description>;
        break;
      }

      case SFFProblemType.Type7:
        ret = (
          <Description>
            <div>
              {parseParagraph(this.question as string, 'Inline-paragraph')}
            </div>
            <p className='ProblemInput-p7-question'>
              Which statement could be removed to improve this response?
            </p>
          </Description>
        );
    }
    return ret;
  }

  public get score(): number {
    return (
      this.options.reduce(
        (result, option): number =>
          (option.isAnsweredCorrectly ? 1 : 0) + result,
        0
      ) / this.options.length
    );
  }

  public correctReasons: () => TagText[] = () => {
    const ret = this.options.reduce((c, o): TagText[] => {
      if (o.isCorrectResponse && o.reason) {
        o.reason.forEach((element) => {
          c.push(element);
        });
      }
      return c;
    }, [] as TagText[]);
    return ret;
  };
}
